import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { ApiError, api } from "../api.ts";
import { useMe } from "../session.ts";
import { CreditMemo } from "../site/visuals.tsx";
import { Button, cx, ErrorText, Field, Input, Select } from "../ui.tsx";

/**
 * Public marketing pages and the early-access list. Two audiences, one page: finance and
 * procurement leaders (/welcome) and PE operating partners (/partners), so each campaign lands
 * on the message its ad promised. Claims here stay within what the product does today.
 */
type Audience = "company" | "partner";

const COPY: Record<
  Audience,
  { eyebrow: string; title: string; lead: string; cta: string; interest: "audit" | "portfolio" }
> = {
  company: {
    eyebrow: "For finance and procurement leaders",
    title: "Your software vendors owe you money. ninekeep gets it back.",
    lead: "When a software vendor misses its uptime promise, your contract often owes you money back. But only if someone notices, works out the amount, and asks before the deadline. ninekeep does all three, from your own contracts.",
    cta: "Join the waitlist",
    interest: "audit",
  },
  partner: {
    eyebrow: "For PE operating partners",
    title: "Savings at every portfolio company, from one rollout.",
    lead: "Every portfolio company pays software vendors that miss their promises, and nobody asks for the money back. ninekeep reads each company's contracts, sends the claims, and keeps each vendor's track record for the next renewal.",
    cta: "Join the waitlist",
    interest: "portfolio",
  },
};

const STEPS = [
  {
    title: "Reads your contracts",
    body: "Upload your vendor contracts. ninekeep finds what each vendor promised, like uptime and support response times, and what you get back for each miss.",
  },
  {
    title: "Monitors every vendor",
    body: "ninekeep checks status pages, runs its own checks, and reads the vendor's emails. It measures each vendor against your contract, not their public page.",
  },
  {
    title: "Shows the math",
    body: "Each month it works out what you're owed, step by step, so anyone can check the numbers.",
  },
  {
    title: "Files and follows up",
    body: "It writes the claim with proof attached, sends it before the deadline, and follows it until the money comes off your bill.",
  },
];

const WHY = [
  [
    "Deadlines are short",
    "Some contracts give you only 15 days after the month ends to ask. Some want you to speak up during the outage.",
  ],
  [
    "The proof is on you",
    "Vendors rarely pay you back on their own. You have to show the outage, how long it lasted, and what the contract says.",
  ],
  [
    "Nobody owns it",
    "IT sees the outage. Finance pays the bill. Procurement holds the contract. Nobody asks for the money back.",
  ],
] as const;

const FAQ: [string, ReactNode][] = [
  [
    "When can I start?",
    "Today. Tell us about your company and we'll set up your account and your free audit.",
  ],
  [
    "What will the free audit include?",
    "ninekeep reads up to 5 of your vendor contracts and checks the last 12 months. It shows what each vendor owed you, with claims ready for anything you can still ask for.",
  ],
  [
    "What will it cost?",
    "The audit is free. After that, it's $4 per vendor a month (at least 5 vendors), plus 15% of the money ninekeep gets back. Every company pays the same.",
  ],
  [
    "Which vendors does it cover?",
    "Any software or cloud vendor whose contract promises uptime. ninekeep also tells you which vendors give nothing back at all. That's good to know when you renew.",
  ],
  [
    "Is my data safe?",
    "Every account signs in with a second step, and each company's data stays private to that company. Single sign-on is available too.",
  ],
  [
    "Does ninekeep contact vendors without me?",
    "Only if you turn that on. Normally, you read each claim before it goes out.",
  ],
];

const UTM_KEY = "ninekeep.utm";
type Utm = { source?: string; medium?: string; campaign?: string; content?: string; term?: string };

/** First-touch campaign for this visit: kept for the session so later page views don't overwrite it. */
function useCampaign() {
  const [utm, setUtm] = useState<{ utm: Utm; referrer?: string; landingPath: string }>({
    utm: {},
    landingPath: location.pathname,
  });
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const fromUrl: Utm = {};
    for (const k of ["source", "medium", "campaign", "content", "term"] as const) {
      const v = q.get(`utm_${k}`);
      if (v) fromUrl[k] = v.slice(0, 150);
    }
    let saved: typeof utm | null = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(UTM_KEY) ?? "null");
    } catch {
      /* storage unavailable */
    }
    // Keep the first campaign that tagged this visit; an untagged first page view doesn't count.
    const keep = saved?.utm?.source || !fromUrl.source ? saved : null;
    const next = keep ?? {
      utm: fromUrl,
      referrer: document.referrer ? document.referrer.slice(0, 500) : undefined,
      landingPath: location.pathname,
    };
    try {
      if (!keep) sessionStorage.setItem(UTM_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable */
    }
    setUtm(next);
  }, []);
  return utm;
}

/** The waitlist form's one-use ticket: how ninekeep tells people from scripts. */
const fetchTicket = () =>
  api
    .get<{ ticket: string }>("/waitlist/ticket")
    .then((r) => r.ticket)
    .catch(() => null);

const VENDOR_OPTIONS = [
  ["aws", "AWS"],
  ["azure", "Azure"],
  ["gcp", "Google Cloud"],
  ["salesforce", "Salesforce"],
  ["atlassian", "Atlassian"],
  ["snowflake", "Snowflake"],
  ["datadog", "Datadog"],
  ["slack", "Slack"],
  ["zoom", "Zoom"],
  ["okta", "Okta"],
  ["other", "Other"],
] as const;

export function WaitlistForm({ audience }: { audience: Audience }) {
  const campaign = useCampaign();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: COPY[audience].interest as string,
    spend: "",
    website: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  // Fetched as the page loads.
  const ticket = useRef<string | null>(null);
  useEffect(() => {
    void fetchTicket().then((t) => {
      ticket.current = t;
    });
  }, []);
  const [vendors, setVendors] = useState<string[]>([]);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (!ticket.current) {
        // The first fetch failed: get one now and give it the moment a person would take.
        ticket.current = await fetchTicket();
        await new Promise((r) => setTimeout(r, 3_500));
      }
      const used = ticket.current;
      ticket.current = null;
      // Ready for a second sign-up from this page.
      void fetchTicket().then((t) => {
        ticket.current = t;
      });
      await api.post("/waitlist", {
        ...form,
        ticket: used ?? undefined,
        vendors,
        spend: form.spend || undefined,
        ...campaign,
      });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div className="rounded-card border border-line bg-surface p-6" role="status">
        <h3 className="text-lg font-semibold">You're on the waitlist.</h3>
        <p className="mt-2 text-muted">
          Thanks, {form.name.split(" ")[0]}. We're letting companies in a few at a time. When it's
          your turn, we'll set up your account and email {form.email} a link to sign in and start
          your{" "}
          {form.interest === "portfolio"
            ? "audit for each company"
            : form.interest === "advisor"
              ? "audit for each client"
              : "free audit"}
          .
        </p>
      </div>
    );

  const fieldError = (path: string) =>
    error instanceof ApiError ? error.issues?.find((i) => i.path === path)?.message : undefined;

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-card border border-line bg-surface p-6"
      noValidate
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" error={fieldError("name")}>
          {(id) => (
            <Input id={id} required autoComplete="name" value={form.name} onChange={set("name")} />
          )}
        </Field>
        <Field label="Work email" error={fieldError("email")}>
          {(id) => (
            <Input
              id={id}
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={set("email")}
            />
          )}
        </Field>
        <Field label={audience === "partner" ? "Firm" : "Company"} error={fieldError("company")}>
          {(id) => (
            <Input
              id={id}
              required
              autoComplete="organization"
              value={form.company}
              onChange={set("company")}
            />
          )}
        </Field>
        <Field label="Yearly SaaS and cloud spend">
          {(id) => (
            <Select id={id} value={form.spend} onChange={set("spend")}>
              <option value="">Prefer not to say</option>
              <option value="under_500k">Under $500k</option>
              <option value="500k_5m">$500k–$5M</option>
              <option value="5m_25m">$5M–$25M</option>
              <option value="over_25m">Over $25M</option>
            </Select>
          )}
        </Field>
      </div>
      <Field label="What problem are you trying to solve?">
        {(id) => (
          <Select id={id} value={form.interest} onChange={set("interest")}>
            <option value="audit">Vendors go down and we never get credited</option>
            <option value="terms">We don't know what our contracts promise</option>
            <option value="outage">A recent outage cost us</option>
            <option value="renewal">We want leverage for a vendor renewal</option>
            <option value="portfolio">Our portfolio companies are owed credits</option>
            <option value="advisor">The clients I advise are owed credits</option>
            <option value="exploring">Just exploring</option>
          </Select>
        )}
      </Field>
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1.5 text-sm font-medium">Which vendors do you pay for?</legend>
        <div className="flex flex-wrap gap-2">
          {VENDOR_OPTIONS.map(([id, label]) => {
            const on = vendors.includes(id);
            return (
              <button
                key={id}
                type="button"
                aria-pressed={on}
                onClick={() => setVendors((v) => (on ? v.filter((x) => x !== id) : [...v, id]))}
                className={cx(
                  "h-8 rounded-full border px-3.5 text-sm font-medium",
                  on
                    ? "border-ink bg-ink text-bg"
                    : "border-line-strong bg-surface text-muted hover:text-ink",
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted">Optional: pick any.</p>
      </fieldset>
      {/* Hidden from people; bots fill it in. */}
      <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
        </label>
      </div>
      {error && !(error instanceof ApiError && error.issues?.length) ? (
        <ErrorText error={error} />
      ) : null}
      <Button type="submit" variant="primary" busy={busy} className="h-11 text-base">
        {COPY[audience].cta}
      </Button>
      <p className="text-xs text-faint">
        Joining is free. We'll only use this to contact you about ninekeep.
      </p>
    </form>
  );
}

export function Landing({ audience }: { audience: Audience }) {
  const { data: me } = useMe();
  const copy = COPY[audience];
  useEffect(() => {
    document.title =
      audience === "partner"
        ? "ninekeep for private equity firms"
        : "ninekeep: get back what your vendors owe you";
    return () => {
      document.title = "ninekeep";
    };
  }, [audience]);
  const toForm = () => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-dvh">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 py-5 sm:px-6">
        <Link to="/welcome" className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="font-display text-xl font-bold tracking-tight">ninekeep</span>
        </Link>
        <nav className="flex items-center gap-1 text-sm sm:gap-3">
          <Link
            to={audience === "partner" ? "/welcome" : "/partners"}
            className="hidden rounded-chip px-3 py-2 text-muted hover:text-ink sm:block"
          >
            {audience === "partner" ? "For companies" : "For PE firms"}
          </Link>
          <Link
            to={me?.user ? "/" : "/signin"}
            className="rounded-chip px-3 py-2 text-muted hover:text-ink"
          >
            {me?.user ? "Open app" : "Sign in"}
          </Link>
          <Button variant="primary" onClick={toForm}>
            {copy.cta}
          </Button>
        </nav>
      </header>

      <main id="main" tabIndex={-1} className="focus:outline-none">
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pt-10 pb-16 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:pt-16">
          <div className="min-w-0">
            <p className="text-sm font-semibold tracking-wide text-accent uppercase">
              {copy.eyebrow}
            </p>
            <h1 className="mt-4 font-display text-5xl leading-display font-bold tracking-tight text-balance sm:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-5 max-w-xl text-lg text-muted">{copy.lead}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button variant="primary" className="h-11 px-5 text-base" onClick={toForm}>
                {copy.cta}
              </Button>
              <span className="text-sm text-muted">
                Free: 12 months, up to 5 contracts, no card.
              </span>
            </div>
          </div>
          <CreditMemo />
        </section>

        <section className="border-y border-line bg-surface">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <h2 className="font-display text-3xl font-bold tracking-tight">
              Why the money goes unclaimed
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-3">
              {WHY.map(([t, b]) => (
                <div key={t}>
                  <h3 className="font-semibold">{t}</h3>
                  <p className="mt-2 text-muted">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <h2 className="font-display text-3xl font-bold tracking-tight">How ninekeep works</h2>
          <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <li key={s.title} className="rounded-card border border-line bg-surface p-5">
                <span className="tabular flex size-7 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent">
                  {i + 1}
                </span>
                <h3 className="mt-3 font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted">{s.body}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 text-muted">
            It covers uptime, support response times and outage notice, which status-page tools
            don't track.
          </p>
        </section>

        {audience === "partner" && (
          <section className="border-y border-line bg-surface">
            <div className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-3">
              <div>
                <h2 className="font-display text-3xl font-bold tracking-tight">
                  Built for firms that own many companies
                </h2>
                <p className="mt-3 text-muted">One introduction, every company covered.</p>
              </div>
              {[
                [
                  "One rollout",
                  "Introduce ninekeep once. Each company will get its own free audit and its own account.",
                ],
                [
                  "Each company's total",
                  "Each company sees what it was owed, and what came back. A single view across all of them is coming.",
                ],
                [
                  "Leverage at renewal",
                  "The same vendor often serves several of your companies. Its track record across all of them makes a stronger case at renewal.",
                ],
              ]
                .slice(0, 3)
                .map(([t, b]) => (
                  <div key={t} className="lg:col-span-1">
                    <h3 className="font-semibold">{t}</h3>
                    <p className="mt-2 text-muted">{b}</p>
                  </div>
                ))}
            </div>
          </section>
        )}

        <section id="join" className="scroll-mt-6">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1.1fr]">
            <div className="min-w-0">
              <h2 className="font-display text-3xl font-bold tracking-tight">
                {audience === "partner" ? "Get your companies started" : "Get started"}
              </h2>
              <p className="mt-3 text-muted">
                Tell us about your company and we'll set up your free audit: up to 5 contracts, the
                last 12 months, with the math for each.
              </p>
              <dl className="mt-8 flex flex-col gap-5">
                {FAQ.map(([q, a]) => (
                  <div key={q}>
                    <dt className="font-semibold">{q}</dt>
                    <dd className="mt-1 text-muted">{a}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className={cx("min-w-0 lg:sticky lg:top-6 lg:self-start")}>
              <WaitlistForm audience={audience} />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-line">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-faint sm:px-6">
          <span>© {new Date().getFullYear()} ninekeep</span>
          <div className="flex gap-4">
            <Link to="/welcome" className="hover:text-ink">
              For companies
            </Link>
            <Link to="/partners" className="hover:text-ink">
              For PE firms
            </Link>
            <Link to="/signin" className="hover:text-ink">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
